import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DropdownModule, InputTextModule, MultiSelectModule, SelectItem } from 'primeng';
import { FormControl, FormGroup } from '@angular/forms';
import { VmeCommonModule } from '../../../vme-common.module';
import { ControlsOfFlat } from '../../../utils/controls-of';
import { OrderBookingHealthCheckService } from '../../../services/order-booking-health-check.service';
import { IOrderBookingSearchFilterValues } from '../../../interfaces/order-booking-health-check/order-booking-search';
import { IScopedSelectItem } from '../../../interfaces/order-booking-health-check/order-booking-health-check-filter-dto';

@Component({
  selector: 'order-booking-filters',
  standalone: true,
  imports: [CommonModule, DropdownModule, InputTextModule, MultiSelectModule, VmeCommonModule],
  templateUrl: './order-booking-filters.component.html',
})
export class OrderBookingFiltersComponent implements OnInit {
  // Emits values rather than controls, so a later edit cannot change what was searched for.
  @Output() searchClicked: EventEmitter<IOrderBookingSearchFilterValues> =
    new EventEmitter<IOrderBookingSearchFilterValues>();

  filterForm: FormGroup<ControlsOfFlat<IOrderBookingSearchFilterValues>>;

  customers: SelectItem[] = [];
  checkTypes: SelectItem[] = [];
  issueProgress: SelectItem[] = [];

  // Narrowed to the scope chosen above them. Fields rather than getters: PrimeNG realigns an open
  // panel whenever the options array is a new instance, which a getter makes it on every CD pass.
  departmentOptions: IScopedSelectItem[] = [];
  teamOptions: IScopedSelectItem[] = [];

  private departments: IScopedSelectItem[] = [];
  private teams: IScopedSelectItem[] = [];

  // Open work always shows; this only governs how far back closed-off bookings stay visible.
  raisedWithinDayOptions: SelectItem[] = [
    { label: 'Raised: last 24 hours', value: 1 },
    { label: 'Raised: last 3 days', value: 3 },
    { label: 'Raised: last 7 days', value: 7 },
    { label: 'Raised: last 30 days', value: 30 },
    { label: 'Raised: any time', value: 0 },
  ];
  bookingStatuses: SelectItem[] = [];
  priorities: SelectItem[] = [];

  supplierAssignedOptions: SelectItem[] = [
    { label: 'Allocation: any', value: null },
    { label: 'Unallocated', value: false },
    { label: 'Allocated', value: true },
  ];

  constructor(private orderBookingHealthCheckService: OrderBookingHealthCheckService) {
    this.initFilters();
  }

  initFilters() {
    this.filterForm = new FormGroup<ControlsOfFlat<IOrderBookingSearchFilterValues>>({
      customerIds: new FormControl<number[]>(null),
      departmentIds: new FormControl<number[]>(null),
      teamIds: new FormControl<number[]>(null),
      checkTypes: new FormControl<number[]>(null),
      issueProgress: new FormControl<number[]>(null),
      raisedWithinDays: new FormControl<number>(1),
      supplierAssigned: new FormControl<boolean>(null),
      priorities: new FormControl<number[]>(null),
      bookingStatuses: new FormControl<number[]>(null),
      searchText: new FormControl<string>(null),
    });

    this.filterForm.controls.customerIds.valueChanges.subscribe(() => this.applyScope());
    this.filterForm.controls.departmentIds.valueChanges.subscribe(() => this.applyScope());

    this.applyScope();
  }

  // A team only means something inside its department, and a department inside its customer, so
  // each list follows the one above it, and a selection that falls out of scope goes with it.
  private applyScope() {
    const controls = this.filterForm.controls;

    this.departmentOptions = this.within(this.departments, controls.customerIds.value);
    this.prune(controls.departmentIds, this.departmentOptions);

    this.teamOptions = this.within(this.teams, controls.departmentIds.value);
    this.prune(controls.teamIds, this.teamOptions);
  }

  // Nothing chosen above means nothing to narrow by, so the whole list stays on offer.
  private within(options: IScopedSelectItem[], parentIds: number[]): IScopedSelectItem[] {
    return parentIds?.length ? options.filter((x) => parentIds.includes(x.parentId)) : options;
  }

  // Silent, or pruning the departments would re-run the whole cascade from the middle.
  private prune(control: FormControl<number[]>, options: IScopedSelectItem[]) {
    const kept = control.value?.filter((id) => options.some((x) => x.value === id));

    if (kept?.length !== control.value?.length)
      control.setValue(kept?.length ? kept : null, { emitEvent: false });
  }

  async ngOnInit() {
    const data = await this.orderBookingHealthCheckService.getFilterOptions();

    this.customers = data.customers;
    this.departments = data.departments;
    this.teams = data.teams;
    this.checkTypes = data.checkTypes;
    this.issueProgress = data.issueProgress;
    this.bookingStatuses = data.bookingStatuses;

    // Priority labels are not stored anywhere, so they are spelled out here as they are on the
    // Supplier Risk Management filters.
    this.priorities = [
      { value: 0, label: 'Unset Priority' },
      { value: 1, label: 'W1 Critical Priority' },
      { value: 2, label: 'W2 High Priority' },
      { value: 3, label: 'W3 Medium Priority' },
      { value: 4, label: 'W4 Low Priority' },
    ];

    this.applyScope();
  }

  protected resetClicked() {
    this.initFilters();
    this.searchClicked.emit();
  }
}
